<template>
  <div class="page-container">
    <div class="wz-page-header">
      <h1 class="wz-page-title">{{ editing ? 'Editar licencia' : 'Nueva licencia' }}</h1>
    </div>

    <wz-card>
      <template #header>
        <div class="wz-card-title">Datos de la licencia</div>
      </template>

      <!-- Empresa y colaborador -->
      <div class="flex flex-wrap -mx-2">
        <div class="w-full md:w-1/2 mb-3 px-2">
          <wz-select
            label="Empresa"
            required
            :items="companies"
            :value="form.companyId"
            defaultOption="Seleccione empresa"
            :disabled="lockBase"
            :error="$v.form.companyId.$error ? 'La empresa es obligatoria' : ''"
            @change="onCompanyChange"
          />
        </div>
        <div class="w-full md:w-1/2 mb-3 px-2">
          <wz-select-search
            label="Colaborador"
            required
            :items="employees"
            :value="form.employeeId"
            defaultOption="Buscar por nombre o documento"
            :disabled="lockBase || !form.companyId"
            :error="$v.form.employeeId.$error ? 'El colaborador es obligatorio' : ''"
            @change="v => form.employeeId = v"
          />
        </div>
      </div>

      <!-- Tipo de licencia -->
      <div class="mb-3" v-if="form.employeeId">
        <wz-select
          label="Tipo de licencia"
          required
          :items="leaveTypeOptions"
          v-model="form.leaveTypeId"
          defaultOption="Seleccione tipo"
          :disabled="lockBase"
          :error="$v.form.leaveTypeId.$error ? 'El tipo de licencia es obligatorio' : ''"
          @input="onTypeChange"
        />
      </div>

      <template v-if="selectedType">
        <hr class="my-4">

        <!-- PATERNIDAD -->
        <div v-if="isPaternidad" class="flex flex-wrap -mx-2">
          <div class="w-full md:w-1/2 mb-3 px-2">
            <wz-select label="Subtipo" :items="subtypeOptions" v-model="form.subtype" :disabled="lockBase" @input="recomputeEndDate" />
          </div>
          <div class="w-full md:w-1/2 mb-3 px-2">
            <wz-input label="Nombre del familiar (hijo/a)" v-model="form.beneficiaryName" :disabled="lockBase" />
          </div>
        </div>

        <!-- FALLECIMIENTO -->
        <div v-if="isFallecimiento" class="flex flex-wrap -mx-2">
          <div class="w-full md:w-1/2 mb-3 px-2">
            <wz-select label="Parentesco" :items="relationshipOptions" v-model="form.relationship" :disabled="lockBase" />
          </div>
          <div class="w-full md:w-1/2 mb-3 px-2">
            <wz-input label="Nombre del familiar" v-model="form.beneficiaryName" :disabled="lockBase" />
          </div>
          <div class="w-full md:w-1/2 mb-3 px-2">
            <wz-select label="Ubicación" :items="eventLocationOptions" v-model="form.eventLocation" :disabled="lockBase" @input="onEventLocationChange" />
          </div>
          <div class="w-full md:w-1/2 mb-3 px-2" v-if="form.eventLocation === 'OTRA_LOCALIDAD'">
            <wz-input
              label="Días de extensión"
              type="number"
              min="0"
              v-model.number="form.extensionDays"
              :disabled="lockBase && !enCursoLock"
              @input="recomputeEndDate"
            />
          </div>
        </div>

        <!-- ENFERMEDAD GRAVE FAMILIAR -->
        <div v-if="isEnfGrave" class="flex flex-wrap -mx-2">
          <div class="w-full md:w-1/2 mb-3 px-2">
            <wz-select label="Parentesco" :items="enfGraveRelationshipOptions" v-model="form.relationship" :disabled="lockBase" />
          </div>
          <div class="w-full md:w-1/2 mb-3 px-2">
            <wz-input label="Nombre del familiar" v-model="form.beneficiaryName" :disabled="lockBase" />
          </div>
          <div class="w-full px-2">
            <wz-alert variant="info" icon="ri-information-line">
              Los días adicionales a los 7 remunerados por ley requieren acuerdo con el empleador sobre su compensación.
            </wz-alert>
          </div>
        </div>

        <!-- EXÁMENES ONCOLÓGICOS PREVENTIVOS -->
        <div v-if="isExamenesOncologicos" class="flex flex-wrap -mx-2">
          <div class="w-full px-2 mb-3">
            <wz-alert variant="info" icon="ri-information-line">
              2 días hábiles al año, remunerada y compensable (el trabajador repone las horas). Documentación requerida: constancia de atención.
            </wz-alert>
          </div>
        </div>

        <!-- DETECCIÓN DE CÁNCER DE MAMA Y CUELLO UTERINO -->
        <div v-if="isCancerMamaCuelloUterino" class="flex flex-wrap -mx-2">
          <div class="w-full px-2 mb-3">
            <wz-alert variant="info" icon="ri-information-line">
              1 día hábil al año, remunerada. Solo aplica a colaboradoras. Documentación: programación de cita (con 3 días de anticipación) y constancia de atención posterior.
            </wz-alert>
          </div>
        </div>

        <!-- PERMISO INTERNO (otros tipos) -->
        <div v-if="isInternal" class="flex flex-wrap -mx-2">
          <div class="w-full md:w-1/2 mb-3 px-2">
            <wz-input label="Nombre del familiar (si aplica)" v-model="form.beneficiaryName" :disabled="lockBase" />
          </div>
          <div class="w-full md:w-1/2 mb-3 px-2">
            <wz-select label="Parentesco (si aplica)" :items="relationshipOptions" v-model="form.relationship" defaultOption="No aplica" :disabled="lockBase" />
          </div>
          <div class="w-full md:w-1/3 mb-3 px-2">
            <wz-input label="Días" type="number" min="1" v-model.number="form.daysField" :disabled="lockBase" @input="recomputeEndDate" />
          </div>
          <div class="w-full md:w-1/3 mb-3 px-2">
            <wz-select label="Remunerado" :items="boolOptions" v-model="form.isRemunerated" :disabled="lockBase" />
          </div>
        </div>

        <!-- Fechas -->
        <div class="flex flex-wrap -mx-2">
          <div class="w-full md:w-1/3 mb-3 px-2">
            <wz-datepicker
              label="Fecha de inicio"
              required
              v-model="form.startDate"
              :disabled="lockBase"
              :error="$v.form.startDate.$error ? 'La fecha de inicio es obligatoria' : ''"
              @input="recomputeEndDate"
            />
          </div>
          <div class="w-full md:w-1/3 mb-3 px-2">
            <wz-datepicker
              label="Fecha de fin"
              required
              v-model="form.endDate"
              :disabled="!endDateManual"
              :error="$v.form.endDate.$error ? 'La fecha de fin es obligatoria' : ''"
            />
          </div>
          <div class="w-full md:w-1/3 flex items-center px-2">
            <div v-if="totalDays" class="mt-3">
              <wz-badge variant="success-solid">{{ totalDays }} días calendario</wz-badge>
            </div>
          </div>
        </div>

        <!-- Comunes: documento + observaciones -->
        <hr class="my-4">
        <div class="mb-3">
          <label class="wz-form-label">Documento adjunto <span class="text-muted text-sm ml-1">(PDF/JPG/PNG, opcional)</span></label>
          <wz-file-input
            :key="fileKey"
            class="w-full mt-2"
            id-file="leave-attachment"
            :accept-file="['.pdf', '.jpg', '.jpeg', '.png']"
            @onChange="onFileChange"
          />
        </div>
        <div class="mb-3">
          <wz-textarea label="Observaciones" v-model="form.observations" :rows="3" placeholder="Observaciones adicionales" />
        </div>
      </template>

      <div class="text-right mt-4">
        <wz-button variant="light" class="mr-2" @click="$router.push({ name: 'licenses-list' })">Cancelar</wz-button>
        <wz-button variant="primary" @click="save()">Guardar</wz-button>
      </div>
    </wz-card>
  </div>
</template>

<script>
import { Alert } from "@wizepe/wizeui"
import { required } from 'vuelidate/lib/validators'
import { LeaveService, LeaveTypeService, EmployeeService, CompanyService } from '@/core/services'
import { Constants as LocalConstants } from '@/core/utils'

export default {
  name: 'LeaveCreate',
  data() {
    return {
      editing: false,
      leaveStatus: null,
      companies: [],
      employees: [],
      leaveTypes: [],
      selectedFile: null,
      fileKey: 0,
      form: {
        companyId: null,
        employeeId: null,
        leaveTypeId: null,
        subtype: 'NORMAL',
        beneficiaryName: null,
        relationship: null,
        eventLocation: null,
        extensionDays: 0,
        daysField: null,
        startDate: null,
        endDate: null,
        observations: null,
        isRemunerated: true,
      },
      subtypeOptions: Object.values(LocalConstants.LeavePaternitySubtype).map(s => ({ key: s.key, label: s.label })),
      relationshipOptions: Object.values(LocalConstants.LeaveRelationship).map(r => ({ key: r.key, label: r.label })),
      enfGraveRelationshipOptions: Object.values(LocalConstants.LeaveRelationship)
        .filter(r => r.key !== 'HERMANO').map(r => ({ key: r.key, label: r.label })),
      eventLocationOptions: Object.values(LocalConstants.LeaveEventLocation).map(l => ({ key: l.key, label: l.label })),
      boolOptions: [{ key: true, label: 'Sí' }, { key: false, label: 'No' }],
    }
  },
  validations() {
    return {
      form: {
        companyId:   { required },
        employeeId:  { required },
        leaveTypeId: { required },
        startDate:   { required },
        endDate:     { required },
      },
    }
  },
  computed: {
    leaveTypeOptions() {
      return this.leaveTypes.map(t => ({ key: t.id, label: t.name }))
    },
    selectedType() {
      return this.leaveTypes.find(t => t.id === this.form.leaveTypeId) || null
    },
    code() {
      return this.selectedType ? this.selectedType.code : null
    },
    isPaternidad()    { return this.code === LocalConstants.LeaveTypeCode.PATERNIDAD },
    isFallecimiento() { return this.code === LocalConstants.LeaveTypeCode.FALLECIMIENTO },
    isEnfGrave()      { return this.code === LocalConstants.LeaveTypeCode.ENFERMEDAD_GRAVE_FAMILIAR },
    isExamenesOncologicos()     { return this.code === LocalConstants.LeaveTypeCode.EXAMENES_ONCOLOGICOS },
    isCancerMamaCuelloUterino() { return this.code === LocalConstants.LeaveTypeCode.CANCER_MAMA_CUELLO_UTERINO },
    isInternal() {
      return !!this.selectedType && !this.isPaternidad && !this.isFallecimiento && !this.isEnfGrave
        && !this.isExamenesOncologicos && !this.isCancerMamaCuelloUterino
    },
    enCursoLock() {
      return this.editing && this.leaveStatus === 'EN_CURSO'
    },
    // En EN_CURSO solo se editan endDate/extension/observations; el resto bloqueado
    lockBase() {
      return this.enCursoLock
    },
    // endDate manual cuando: enfermedad grave o edición EN_CURSO; en los demás se autocalcula
    endDateManual() {
      return this.isEnfGrave || this.enCursoLock
    },
    totalDays() {
      if (!this.form.startDate || !this.form.endDate) return null
      const start = new Date(this.form.startDate + 'T00:00:00')
      const end   = new Date(this.form.endDate   + 'T00:00:00')
      const diff  = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1
      return diff > 0 ? diff : null
    },
  },
  methods: {
    async onCompanyChange(companyId) {
      this.form.companyId = companyId || null
      this.form.employeeId = null
      this.form.leaveTypeId = null
      this.employees = []
      this.leaveTypes = []
      if (!companyId) return
      await Promise.all([this.loadEmployees(companyId), this.loadLeaveTypes(companyId)])
    },
    async loadEmployees(companyId) {
      try {
        const resp = await EmployeeService.listByCompany(companyId)
        const seen = new Set()
        this.employees = (resp.payload || [])
          .filter(e => e && e.id != null && !seen.has(e.id) && seen.add(e.id))
          .map(e => ({ key: e.id, label: e.description ?? '' }))
      } catch (e) { this.employees = [] }
    },
    async loadLeaveTypes(companyId) {
      try {
        const resp = await LeaveTypeService.list({ companyId, status: 1 })
        this.leaveTypes = resp.payload || []
      } catch (e) { this.leaveTypes = [] }
    },
    onTypeChange() {
      // Reset de campos condicionales según el nuevo tipo
      this.form.subtype = 'NORMAL'
      this.form.eventLocation = null
      this.form.extensionDays = 0
      this.form.relationship = null
      this.form.endDate = null
      if (this.isPaternidad) {
        this.form.relationship = 'HIJO'
      } else if (this.isInternal) {
        this.form.daysField = this.selectedType.defaultDays || null
        this.form.isRemunerated = this.selectedType.isRemunerated !== false
      }
      this.recomputeEndDate()
    },
    onEventLocationChange() {
      if (this.form.eventLocation !== 'OTRA_LOCALIDAD') this.form.extensionDays = 0
      this.recomputeEndDate()
    },
    computedDays() {
      if (this.isPaternidad) {
        const st = LocalConstants.LeavePaternitySubtype[this.form.subtype]
        return st ? st.days : 10
      }
      if (this.isFallecimiento) {
        return 5 + (this.form.eventLocation === 'OTRA_LOCALIDAD' ? (this.form.extensionDays || 0) : 0)
      }
      if (this.isInternal) {
        return this.form.daysField || null
      }
      if (this.isExamenesOncologicos || this.isCancerMamaCuelloUterino) {
        return this.selectedType.defaultDays || null
      }
      return null
    },
    recomputeEndDate() {
      if (this.endDateManual) return
      const days = this.computedDays()
      if (!this.form.startDate || !days) { this.form.endDate = null; return }
      const start = new Date(this.form.startDate + 'T00:00:00')
      start.setDate(start.getDate() + days - 1)
      this.form.endDate = start.toISOString().split('T')[0]
    },
    onFileChange(files) {
      this.selectedFile = files && files.length ? files[0] : null
    },
    buildPayload() {
      return {
        companyId: this.form.companyId,
        employeeId: this.form.employeeId,
        leaveTypeId: this.form.leaveTypeId,
        subtype: this.isPaternidad ? this.form.subtype : null,
        beneficiaryName: this.form.beneficiaryName || null,
        relationship: this.form.relationship || null,
        startDate: this.form.startDate,
        endDate: this.form.endDate,
        eventLocation: this.isFallecimiento ? this.form.eventLocation : null,
        extensionDays: this.form.extensionDays || 0,
        observations: this.form.observations || null,
        isRemunerated: this.form.isRemunerated,
      }
    },
    async save() {
      this.$v.$touch()
      if (this.$v.$error) {
        this.$nextTick(() => {
          const el = document.querySelector('.wz-form-error-msg, .form-error')
          if (el && this.$smoothScroll) this.$smoothScroll({ scrollTo: el, updateHistory: false, offset: -50 })
        })
        return
      }
      // 1) Guardar la licencia. Si esto falla, no se intenta el documento.
      let saved
      try {
        this.$store.dispatch('app/loading', true)
        const payload = this.buildPayload()
        const resp = this.editing
          ? await LeaveService.update(this.$route.params.id, payload)
          : await LeaveService.create(payload)
        saved = resp.payload || {}
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al guardar la licencia')
        return
      } finally {
        this.$store.dispatch('app/loading', false)
      }

      // 2) Subir el documento (opcional) de forma independiente: un fallo aquí
      //    no invalida la licencia ya creada.
      let documentFailed = false
      if (this.selectedFile && saved.id) {
        try {
          this.$store.dispatch('app/loading', true)
          await LeaveService.uploadDocument(saved.id, this.selectedFile)
        } catch (e) {
          documentFailed = true
          console.error('Error al subir el documento de la licencia', e)
        } finally {
          this.$store.dispatch('app/loading', false)
        }
      }

      // 3) Feedback diferenciado (Alert.alert = aviso no bloqueante; el servicio no tiene .warning)
      if (documentFailed) {
        Alert.alert('AVISO', 'Licencia creada. Error al subir el documento. Puede adjuntarlo desde el detalle.')
      } else {
        Alert.success(this.editing ? 'Licencia actualizada correctamente' : 'Licencia creada correctamente')
      }
      if (saved.alerts && saved.alerts.length) {
        Alert.alert('AVISO', saved.alerts.join('\n'))
      }
      this.$router.push({ name: 'licenses-detail', params: { id: saved.id || this.$route.params.id } })
    },
    async loadForEdit(id) {
      try {
        this.$store.dispatch('app/loading', true)
        const leave = (await LeaveService.get(id)).payload
        if (leave.status === 'FINALIZADA' || leave.status === 'CANCELADA') {
          Alert.error('Esta licencia no se puede editar')
          this.$router.push({ name: 'licenses-list' })
          return
        }
        this.editing = true
        this.leaveStatus = leave.status
        this.form.companyId = leave.companyId
        await Promise.all([this.loadEmployees(leave.companyId), this.loadLeaveTypes(leave.companyId)])
        this.form.employeeId = leave.employeeId
        this.form.leaveTypeId = leave.leaveTypeId
        this.form.subtype = leave.subtype || 'NORMAL'
        this.form.beneficiaryName = leave.beneficiaryName
        this.form.relationship = leave.relationship
        this.form.eventLocation = leave.eventLocation
        this.form.extensionDays = leave.extensionDays || 0
        this.form.startDate = leave.startDate
        this.form.endDate = leave.endDate
        this.form.observations = leave.observations
        this.form.isRemunerated = leave.isRemunerated
        this.form.daysField = leave.calendarDays
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al cargar la licencia')
        this.$router.push({ name: 'licenses-list' })
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
  },
  async created() {
    try {
      const resp = await CompanyService.listAll()
      this.companies = (resp.payload || []).map(c => ({ key: c.id, label: c.name }))
    } catch (e) { console.error(e) }
    if (this.$route.params.id) await this.loadForEdit(this.$route.params.id)
  },
}
</script>

<style lang="stylus"></style>
